/* eslint-disable react-hooks/rules-of-hooks */

import React, { ReactElement, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Page, Flex, Section, Typography, Box, Loading, ButtonIcon } from 'components';
import styled from 'styled-components';
import { createMarkup, createUrl, request, stripTags } from 'app/utils';
import { useSchool } from 'features/school';
import { AxiosResponse } from 'axios';
import { TeachersDetailsApiResponse, useTeachers } from 'features/teachers';
import { ProductsList, ProductsTeachersApiResponse, useProducts } from 'features/products';
import { useNavigate } from 'react-router-dom';

import usestorage from 'app/utils/storage';

const Tag = styled.span`
  display: inline-flex;
  border: 2px solid ${({ theme }) => theme.colors.primary_400};
  min-height: 40px;
  align-items: center;
  border-radius: 100px;
  padding: 5px 30px;
  margin-right: 10px;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.primary_400};
  text-transform: uppercase;
`;

const teachersDetailsView = (): ReactElement => {
  const [loading, setLoading] = useState(true);

  const { teacherSlug } = useParams();
  const { apiGatewayUrl, schoolUuid } = useSchool();
  const { teachersDetails, setTeachersDetails, clearTeachersDetails } = useTeachers();
  const { productsTeachers, setProductsTeachers, clearProductsTeachers } = useProducts();

  const storage = usestorage('localStorage');
  const currentDashboard = storage.get('currentDashboard');

  const [searchParams] = useSearchParams();
  const currentDashboardArea = searchParams.get('dashboardAreaUuid');

  const navigate = useNavigate();

  const fetch = async () => {
    await Promise.all([
      request({
        url: `/api/v1/School/Teachers/${teacherSlug}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then((response: AxiosResponse<TeachersDetailsApiResponse>) => {
        setTeachersDetails(response.data);
      }),
      request({
        url: `/api/v1/School/TeacherCourses/${teacherSlug}`,
        method: 'get',
        baseURL: apiGatewayUrl,
        params: {
          ...(currentDashboard ? { dashboardUuid: currentDashboard } : {}),
          ...(currentDashboardArea ? { dashboardAreaUuid: currentDashboardArea } : {}),
        },
      }).then((response: AxiosResponse<ProductsTeachersApiResponse>) => {
        setProductsTeachers(response.data);
      }),
    ]).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetch();

    return () => {
      clearTeachersDetails();
      clearProductsTeachers();
    };
  }, []);

  useEffect(() => {
    if (schoolUuid !== '85338cb1-8075-4dcf-afe2-6ce534dcde27') {
      navigate(createUrl('dashboard'));
    }
  }, [schoolUuid]);

  if (loading) return <Loading label="Por favor, aguarde." />;

  return teachersDetails ? (
    <Page title="Professor">
      <Flex flexGrow={0} height={{ d: 540 }} bg="dark_350" pt={30}>
        <Section flexDirection="column" flexGrow={0}>
          <Flex flexGrow={0} flexWrap={{ m: 'wrap', d: 'nowrap' }} alignItems="flex-start">
            <Box flexGrow={0} flexShrink={0} width={{ m: '100%', t: '60%', d: 400 }} position="relative">
              <img src={teachersDetails.coverImageUrl} alt="Nome" />
            </Box>

            <Flex flexDirection="column" ml={{ d: 100 }} pt={{ m: 20, d: 50 }} pb={{ m: 30, d: 0 }}>
              <Typography type="type6" color="white25">
                {stripTags(teachersDetails.phrase)}
              </Typography>

              <Typography type="type11" color="white" fontWeight="bold" mt={2}>
                {teachersDetails.name}
              </Typography>

              <Typography
                type="type2"
                color="white50"
                mt={20}
                lineHeight="1.5"
                data-oao
                dangerouslySetInnerHTML={createMarkup(teachersDetails.initialDescription)}
              ></Typography>

              {Boolean(
                teachersDetails.socialLinkedIn || teachersDetails.socialFacebook || teachersDetails.socialInstagram || teachersDetails.socialTwitter
              ) && (
                <Flex alignItems="center" mt={30}>
                  <Typography type="type2" color="white25" fontWeight="bold" upper mr={1}>
                    Siga nas redes:
                  </Typography>

                  {Boolean(teachersDetails.socialLinkedIn) && (
                    <a href={teachersDetails.socialLinkedIn} target="_blank" rel="noreferrer">
                      <ButtonIcon icon="linkedin" bg="transparent" iconWidth={28} color="gray_600" colorHover="primary_400" ml="12px" />
                    </a>
                  )}

                  {Boolean(teachersDetails.socialFacebook) && (
                    <a href={teachersDetails.socialFacebook} target="_blank" rel="noreferrer">
                      <ButtonIcon icon="facebook" bg="transparent" iconWidth={28} color="gray_600" colorHover="primary_400" ml="12px" />
                    </a>
                  )}

                  {Boolean(teachersDetails.socialInstagram) && (
                    <a href={teachersDetails.socialInstagram} target="_blank" rel="noreferrer">
                      <ButtonIcon icon="instagram" bg="transparent" iconWidth={28} color="gray_600" colorHover="primary_400" ml="12px" />
                    </a>
                  )}

                  {Boolean(teachersDetails.socialTwitter) && (
                    <a href={teachersDetails.socialTwitter} target="_blank" rel="noreferrer">
                      <ButtonIcon icon="twitter" bg="transparent" iconWidth={28} color="gray_600" colorHover="primary_400" ml="12px" />
                    </a>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Section>
      </Flex>

      {!!teachersDetails.professionalDescription && !!teachersDetails.additionalDescription && (
        <Flex flexGrow={0} bg="gray_200">
          <Section flexGrow={0} flexWrap={{ m: 'wrap', d: 'nowrap' }} pt={{ m: 40, d: 80 }} pb={{ m: 40, d: 60 }}>
            <Flex minWidth={{ d: 340 }} flexGrow={0}>
              <Flex flexDirection="column">
                <Typography type="type8" color="dark_350" fontWeight="bold">
                  <div dangerouslySetInnerHTML={createMarkup(teachersDetails.professionalDescription)} />
                </Typography>
              </Flex>
            </Flex>

            <Flex flexGrow={0} pl={{ d: 60 }} mt={{ m: '10px', d: 0 }}>
              <Typography type="type2" color="dark_350" lineHeight="1.5">
                <div dangerouslySetInnerHTML={createMarkup(teachersDetails.additionalDescription)} />
              </Typography>
            </Flex>
          </Section>
        </Flex>
      )}

      <Flex bg="white">
        <Section flexWrap="nowrap" py={60}>
          <Flex flexGrow={1} flexDirection="column">
            <Typography type="type5" color="dark_350">
              Especialidades de ensino
            </Typography>

            {/* <Typography type="type1" color="dark_350" mt={2} lineHeight="1.3">
              Consectetur adipiscing elit. Vivamus justo neque, laoreet at enim ut, dignissim
              consequat lectus. Interdum et malesuada fames ac ante ipsum primis in faucibus. Aenean
              posuere orci ac massa fringilla, eu tincidunt mi lacinia. Mauris fringilla lacus eget
              massa dapibus pulvinar.
            </Typography> */}
            <Box mt={3}>{teachersDetails.tags && teachersDetails.tags.map((tag, index) => <Tag key={index.toFixed()}>{tag.tagText}</Tag>)}</Box>
          </Flex>
        </Section>
      </Flex>

      <Flex py={{ m: 30, t: 60, d: 90 }} bg="gray_200" flexGrow={0}>
        <Section flexDirection="column">
          <Flex justifyContent="space-between" flexWrap={{ m: 'wrap', d: 'nowrap' }} alignItems="flex-end">
            <Flex flexDirection="column">
              <Typography type="type8" fontWeight="bold">
                Cursos que leciona
              </Typography>
            </Flex>

            <Box width="auto" flexGrow={0} justifyContent="flex-end">
              {/* <Link to={createUrl('productsList')}>
              <Button mt={{ m: 20 }}>Todos os cursos</Button>
            </Link> */}
            </Box>
          </Flex>

          {/* <Flex bg="white" mt={30} mb={20} height={60} alignItems="center" justifyContent="center">
          SEARCH
        </Flex> */}

          <ProductsList products={productsTeachers} />
        </Section>
      </Flex>
    </Page>
  ) : (
    <Loading label="Por favor, aguarde." />
  );
};
export default teachersDetailsView;
